import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trainingApi, trainingRequestKey } from '../../../api/training';
import { trainingButton, trainingError, uncertainTrainingOutcome } from './trainingParticipant';

// Keep the operation plan mounted with the gate, including when its dialog closes.
// Each confirmed assignment is skipped on retry; unknown writes keep their key/time.
export default function useTrainingAdminDefer({ assignments, administrative = true, onChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [unknown, setUnknown] = useState(false);
  const [checked, setChecked] = useState(false);
  const [progress, setProgress] = useState(null);
  const active = useRef(true);
  const inFlight = useRef(false);
  const plan = useRef(null);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const refresh = async () => { if (active.current) await onChanged?.(); };
  const defer = async () => {
    if (inFlight.current || (unknown && !checked)) return;
    if (!plan.current) {
      const until = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      plan.current = assignments.map((item) => ({ id: item.id, payload: { until, revision_id: item.revision_id, request_key: trainingRequestKey() }, confirmed: false }));
      setProgress({ saved: 0, total: plan.current.length });
    }
    inFlight.current = true; setBusy(true); setError('');
    try {
      for (const operation of plan.current) {
        if (!active.current) return;
        if (operation.confirmed) continue;
        await trainingApi.snooze(operation.id, operation.payload);
        if (!active.current) return;
        operation.confirmed = true;
        setProgress({ saved: plan.current.filter((item) => item.confirmed).length, total: plan.current.length });
      }
      setUnknown(false); setChecked(false); plan.current = null;
    } catch (failure) {
      if (!active.current) return;
      if (uncertainTrainingOutcome(failure)) { setUnknown(true); setChecked(false); setError(t('trainingParticipant.unknownOutcome')); }
      else { plan.current = null; setUnknown(false); setChecked(false); setError(trainingError(failure, t)); }
    } finally {
      inFlight.current = false;
      if (active.current) { setBusy(false); await refresh(); }
    }
  };
  const check = async () => {
    if (inFlight.current || !plan.current) return;
    inFlight.current = true; setBusy(true);
    try {
      for (const operation of plan.current) {
        if (!active.current) return;
        if (operation.confirmed) continue;
        const response = await trainingApi.assignment(operation.id);
        if (!active.current) return;
        if (response.data.revision_id !== operation.payload.revision_id) {
          plan.current = null; setUnknown(false); setChecked(false);
          setError(t('trainingParticipant.revisionChanged')); return;
        }
        operation.confirmed = Date.parse(response.data.snoozed_until) === Date.parse(operation.payload.until) || ['passed', 'completed', 'exempt', 'withdrawn'].includes(response.data.status);
      }
      const saved = plan.current.filter((item) => item.confirmed).length;
      setProgress({ saved, total: plan.current.length });
      if (saved === plan.current.length) { plan.current = null; setUnknown(false); setChecked(false); setError(''); }
      else { setChecked(true); setError(t('trainingParticipant.sameAttempt')); }
    } catch { if (active.current) setError(t('trainingParticipant.statusFailed')); }
    finally { inFlight.current = false; if (active.current) { setBusy(false); await refresh(); } }
  };
  return <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
    <p className="text-sm text-amber-950">{t(administrative ? 'trainingParticipant.adminDeferHint' : 'trainingParticipant.mandatoryDeferHint')}</p>
    {progress && <p role="status" className="text-sm font-semibold text-amber-950">{t('trainingParticipant.adminDeferProgress', progress)}</p>}
    {error && <p role="alert" className="text-sm text-red-800">{error}</p>}
    <div className="flex flex-wrap gap-2">
      {unknown && <button type="button" className={trainingButton} disabled={busy} onClick={check}>{t('trainingParticipant.checkStatus')}</button>}
      <button type="button" className={trainingButton} disabled={busy || (unknown && !checked) || !assignments.length} onClick={defer}>{t(busy ? 'trainingParticipant.saving' : unknown ? 'trainingParticipant.retrySame' : 'trainingParticipant.adminDefer')}</button>
    </div>
  </div>;
}
